/**
 * EN primary, TR secondary — the whole i18n layer.
 *
 * A tiny catalog and a `t()`. No runtime library: two languages and a few hundred strings
 * do not need one, and the thing that actually matters (layouts sized for the longer of
 * the two, `lang` on the element, diacritics) is CSS and markup, not a formatter.
 *
 * The choice is persisted per browser and `<html lang>` follows it, so a screen reader and
 * the hyphenation engine both get told.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';

import { en } from './en.js';
import type { Catalog, MessageKey } from './en.js';
import { tr } from './tr.js';

export type Locale = 'en' | 'tr';

const CATALOGS: Record<Locale, Catalog> = { en, tr };
const STORAGE_KEY = 'atlas-nook-studio:locale';

export type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string;

/**
 * A key a count can change the wording of: one whose `…One` twin is also in the catalog.
 * `story.meta` has `story.metaOne`, so it is offered here; a key with no twin is not.
 */
// `K extends string` first, so the conditional distributes over the union and each key is
// tested on its own — checking the whole `${MessageKey}One` union at once answers `never`.
type HasOne<K> = K extends string ? (`${K}One` extends MessageKey ? K : never) : never;
export type CountedKey = HasOne<MessageKey>;

export type TranslateCounted = (
  key: CountedKey,
  count: number,
  vars?: Record<string, string | number>,
) => string;

/** *2nd*, *2.* — a number said as a position. */
export type Ordinal = (n: number) => string;

interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translate;
  tCount: TranslateCounted;
  tOrdinal: Ordinal;
}

const I18nContext = createContext<I18nValue | null>(null);

function isLocale(value: unknown): value is Locale {
  return value === 'en' || value === 'tr';
}

function readStored(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // A blocked storage is not a reason to fail to render.
  }
  return 'en';
}

/** `{name}` → `vars.name`. An unknown placeholder is left visible, so a gap is obvious. */
function fill(message: string, vars?: Record<string, string | number>): string {
  if (vars === undefined) return message;
  return message.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in vars ? String(vars[key]) : whole,
  );
}

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  initialLocale?: Locale;
}): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? readStored());

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Same: the preference is a convenience, not state we depend on.
    }
  }, []);

  const value = useMemo<I18nValue>(() => {
    const catalog = CATALOGS[locale];
    const t: Translate = (key, vars) => fill(catalog[key] ?? en[key], vars);

    /*
     * One function, not a branch per string.
     *
     * EN and TR both have exactly two plural categories, and both put `one` at exactly 1, so
     * the rule is `count === 1` and `Intl.PluralRules` would buy nothing here — the same
     * reason this layer has no runtime library at all. Turkish does not inflect a noun after
     * a numeral, so most of its `…One` twins repeat their other form; that sameness is a
     * fact about Turkish and belongs in the catalog rather than in a branch here. A language
     * with `few` / `many` is where this grows a rule, and more twins.
     */
    return {
      locale,
      setLocale,
      t,
      tCount: (key, count, vars) => t(count === 1 ? (`${key}One` as MessageKey) : key, vars),

      /*
       * The ordinal, and it is a formatter rather than a catalog entry.
       *
       * Turkish makes one with a full stop after the digit — *2.* — for every number, and
       * English with a two-letter suffix whose choice is arithmetic, not vocabulary. So there
       * is nothing here for a translator to translate: a rule per language, and the digit.
       * `Intl.PluralRules`' ordinal mode is exactly the English rule and is the runtime's,
       * not a dependency (this layer still has none).
       */
      tOrdinal: (n) => (locale === 'tr' ? `${n}.` : `${n}${englishOrdinalSuffix(n)}`),
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (value === null) throw new Error('useI18n: no I18nProvider above this component');
  return value;
}

/**
 * *st · nd · rd · th* — CLDR's ordinal categories for English, which are a rule about the
 * last one or two digits and not a list. 11th, 12th and 13th are the exceptions the rule
 * already knows about.
 */
const ENGLISH_ORDINALS = new Intl.PluralRules('en', { type: 'ordinal' });
const ENGLISH_SUFFIX: Record<string, string> = { one: 'st', two: 'nd', few: 'rd', other: 'th' };

function englishOrdinalSuffix(n: number): string {
  return ENGLISH_SUFFIX[ENGLISH_ORDINALS.select(n)] ?? 'th';
}

/** The common case — just the translator. */
export function useT(): Translate {
  return useI18n().t;
}

/** The translator for a message whose wording changes with a count. */
export function useTCount(): TranslateCounted {
  return useI18n().tCount;
}

/** A number said as a position — *2nd*, *2.* */
export function useTOrdinal(): Ordinal {
  return useI18n().tOrdinal;
}

/** The locale alone — for copy that is composed in code, like "the accordion". */
export function useLocale(): Locale {
  return useI18n().locale;
}

export type { MessageKey };
export { en, tr };
